export type SearchRecord = Record<string, unknown>;

export interface GlobalSearchNavigationItem {
  label: string;
  href: string;
  icon?: unknown;
}

export interface GlobalSearchSources {
  members?: readonly SearchRecord[];
  vehicles?: readonly SearchRecord[];
  properties?: readonly SearchRecord[];
  tasks?: readonly SearchRecord[];
  supplies?: readonly SearchRecord[];
}

export interface GlobalSearchResult {
  id: string;
  title: string;
  subtitle?: string;
  href: string;
  status?: string;
  kind: "vehicle" | "member" | "property" | "task" | "supply" | "navigation";
  record?: SearchRecord;
}

export interface GlobalSearchGroup {
  key: "vehicles" | "members" | "properties" | "tasks" | "supplies" | "navigation";
  label: string;
  results: GlobalSearchResult[];
  total: number;
}

export interface GlobalSearchModel {
  groups: GlobalSearchGroup[];
  vehicleIntent: boolean;
  vehicleHref: string | null;
}

const stringValue = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

const fields = (record: SearchRecord, names: readonly string[]): string[] =>
  names.map((name) => stringValue(record[name])).filter(Boolean);

export function normalizeSearchText(value: unknown): string {
  return stringValue(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

export function matchesSearch(query: string, values: readonly unknown[]): boolean {
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;
  const haystack = normalizeSearchText(values.map(stringValue).filter(Boolean).join(" "));
  const compact = haystack.replace(/\s+/g, "");
  return tokens.every((token) => haystack.includes(token) || compact.includes(token));
}

function memberName(member: SearchRecord): string {
  const first = stringValue(member.firstName).trim();
  const last = stringValue(member.lastName).trim();
  const firstLast = [first, last].filter(Boolean).join(" ");
  return (
    stringValue(member.displayName).trim() ||
    firstLast ||
    stringValue(member.name).trim() ||
    "Member"
  );
}

function memberFields(member: SearchRecord): string[] {
  return [
    ...fields(member, ["firstName", "lastName", "displayName", "name", "legalName", "email"]),
    memberName(member),
    "member members resident residents tenant tenants",
  ];
}

const vehicleFieldNames = [
  "carMake",
  "carModel",
  "carYear",
  "carColor",
  "licensePlate",
  "licensePlateState",
] as const;

function vehicleFields(member: SearchRecord): string[] {
  return fields(member, vehicleFieldNames);
}

const VEHICLE_MAKES = new Set([
  "acura", "alfa", "aston", "audi", "bentley", "bmw", "buick", "cadillac", "chevrolet",
  "chevy", "chrysler", "citroen", "dacia", "daewoo", "dodge", "ferrari", "fiat", "ford",
  "genesis", "gmc", "honda", "hyundai", "infiniti", "isuzu", "jaguar", "jeep", "kia",
  "lamborghini", "land rover", "lexus", "lincoln", "lotus", "lucid", "maserati", "mazda",
  "mercedes", "mini", "mitsubishi", "nissan", "opel", "peugeot", "pontiac", "porsche",
  "ram", "renault", "rivian", "rolls royce", "saab", "saturn", "scion", "skoda", "smart",
  "subaru", "suzuki", "tesla", "toyota", "vauxhall", "volkswagen", "volvo", "vw",
]);

function hasVehicleData(member: SearchRecord): boolean {
  return vehicleFields(member).some((value) => value.trim().length > 0);
}

function propertyLabel(record: SearchRecord): string {
  return (
    fields(record, ["propertyName", "propertyAddress", "address"])[0] ||
    (record.currentPropertyId != null ? `Property #${record.currentPropertyId}` : "")
  );
}

function recordId(record: SearchRecord, fallback: string): string {
  return stringValue(record.id || record.memberId || record.taskId || fallback);
}

function aliasesFor(item: GlobalSearchNavigationItem): string[] {
  const href = stringValue(item.href).toLowerCase();
  const label = stringValue(item.label).toLowerCase();
  const aliases: string[] = [];
  if (href.includes("member")) aliases.push("profile", "resident", "account");
  if (href.includes("vehicle")) aliases.push("car", "auto", "plate");
  if (href.includes("settings") || label.includes("settings")) aliases.push("preferences", "configuration");
  if (href.includes("account")) aliases.push("profile", "my account");
  if (href === "/app/profile") aliases.push("settings", "notifications", "email preferences", "password", "phone", "timezone", "time zone");
  return aliases;
}

const GROUP_LABELS: Record<GlobalSearchGroup["key"], string> = {
  vehicles: "Vehicles",
  members: "Members",
  properties: "Properties",
  tasks: "Maintenance tasks",
  supplies: "Supplies",
  navigation: "Navigation / Settings",
};

export function buildGlobalSearchModel({
  query,
  sources,
  navigation,
  memberAccess,
  vehicleAccess,
}: {
  query: string;
  sources: GlobalSearchSources;
  navigation: readonly GlobalSearchNavigationItem[];
  memberAccess: boolean;
  vehicleAccess: boolean;
}): GlobalSearchModel {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return { groups: [], vehicleIntent: false, vehicleHref: null };

  const vehicleRows = vehicleAccess
    ? (sources.vehicles ?? sources.members ?? []).filter(
        (member) => hasVehicleData(member) && matchesSearch(normalizedQuery, [
          ...vehicleFields(member), "vehicle vehicles car cars auto",
        ]),
      )
    : [];
  const vehicleIntent = vehicleAccess && (vehicleRows.length > 0 ||
    normalizeSearchText(normalizedQuery).split(/\s+/).some(token => VEHICLE_MAKES.has(token)));
  const vehicleHref = vehicleIntent
    ? `/app/members/vehicles?q=${encodeURIComponent(normalizedQuery)}&status=all`
    : null;

  const vehicles: GlobalSearchResult[] = vehicleRows.map((member) => {
    const name = memberName(member);
    const detail = [
      fields(member, ["carYear", "carMake", "carModel", "carColor"]).join(" ").trim(),
      fields(member, ["licensePlate", "licensePlateState"]).join(" ").trim(),
    ]
      .filter(Boolean)
      .join(" · ");
    return {
      id: `vehicle-${recordId(member, name)}`,
      title: name,
      subtitle: detail || "Vehicle record",
      href: vehicleHref!,
      status: stringValue(member.status) || undefined,
      kind: "vehicle",
      record: member,
    };
  });

  const members: GlobalSearchResult[] = memberAccess
    ? (sources.members ?? [])
        .filter((member) => matchesSearch(normalizedQuery, [...memberFields(member), ...vehicleFields(member)]))
        .map((member) => ({
          id: `member-${recordId(member, memberName(member))}`,
          title: memberName(member),
          subtitle: propertyLabel(member) || undefined,
          href: `/app/members/${encodeURIComponent(recordId(member, ""))}`,
          status: stringValue(member.status) || undefined,
          kind: "member" as const,
          record: member,
        }))
    : [];

  const properties: GlobalSearchResult[] = (sources.properties ?? [])
    .filter((property) => matchesSearch(normalizedQuery, [...fields(property, ["name", "address", "city", "state", "postalCode", "zipCode", "propertyGroup"]), "property properties house houses home homes"]))
    .map((property) => ({
      id: `property-${recordId(property, "")}`,
      title: fields(property, ["name", "address"])[0] || `Property #${recordId(property, "")}`,
      subtitle: fields(property, ["address", "city", "state"]).filter((v) => v !== fields(property, ["name"])[0]).join(", ") || undefined,
      href: `/app/properties/${encodeURIComponent(recordId(property, ""))}`,
      kind: "property" as const,
      record: property,
    }));

  const tasks: GlobalSearchResult[] = (sources.tasks ?? [])
    .filter((task) => matchesSearch(normalizedQuery, [...fields(task, ["id", "padsplitTaskId", "title", "description", "category", "status", "priority", "propertyName", "propertyAddress"]), "task tasks maintenance ticket tickets"]))
    .map((task) => ({
      id: `task-${recordId(task, "")}`,
      title: fields(task, ["title", "description"])[0] || `Maintenance task #${recordId(task, "")}`,
      subtitle: fields(task, ["propertyAddress", "propertyName", "category"]).join(" · ") || undefined,
      href: `/app/tasks?task=${encodeURIComponent(recordId(task, ""))}`,
      status: stringValue(task.status) || undefined,
      kind: "task" as const,
      record: task,
    }));

  const supplies: GlobalSearchResult[] = (sources.supplies ?? [])
    .filter((supply) => matchesSearch(normalizedQuery, [...fields(supply, ["name", "category", "preferredVendor", "description"]), "supply supplies catalog"]))
    .map((supply) => ({
      id: `supply-${recordId(supply, "")}`,
      title: fields(supply, ["name"])[0] || "Supply item",
      subtitle: fields(supply, ["category", "preferredVendor"]).join(" · ") || undefined,
      href: `/app/supplies?tab=catalog&q=${encodeURIComponent(normalizedQuery)}`,
      kind: "supply" as const,
      record: supply,
    }));

  const navResults: GlobalSearchResult[] = navigation
    .filter((item) => matchesSearch(normalizedQuery, [item.label, item.href, ...aliasesFor(item)]))
    .map((item) => ({
      id: `nav-${item.href}`,
      title: item.label,
      subtitle: item.href,
      href: item.href,
      kind: "navigation" as const,
    }));

  const groups: GlobalSearchGroup[] = [];
  const addGroup = (key: GlobalSearchGroup["key"], results: GlobalSearchResult[]) => {
    if (results.length || (key === "vehicles" && vehicleIntent)) groups.push({ key, label: GROUP_LABELS[key], results, total: results.length });
  };
  if (vehicleIntent) addGroup("vehicles", vehicles);
  addGroup("members", members);
  if (!vehicleIntent) addGroup("vehicles", vehicles);
  addGroup("properties", properties);
  addGroup("tasks", tasks);
  addGroup("supplies", supplies);
  addGroup("navigation", navResults);
  return { groups, vehicleIntent, vehicleHref };
}